// SocketProvider.js - UPDATED VERSION
import React, { useEffect, useRef } from "react";
import { socket } from "../common/socket";

const EVENT_MAP = [
  { socketEvent: "connect", browserEvent: "socket:connected" },
  { socketEvent: "disconnect", browserEvent: "socket:disconnected" },
  { socketEvent: "newMessage", browserEvent: "socket:newMessage" },
  { socketEvent: "chatList:update", browserEvent: "socket:chatListUpdate" },
  { socketEvent: "userTyping", browserEvent: "socket:userTyping" },
  { socketEvent: "messagesRead", browserEvent: "socket:messagesRead" },
  { socketEvent: "messageEdited", browserEvent: "socket:messageEdited" },
  { socketEvent: "messagesDeleted", browserEvent: "socket:messagesDeleted" },
  { socketEvent: "error", browserEvent: "socket:error" },
  // Game events
  { socketEvent: "timerUpdate", browserEvent: "socket:timerUpdate" },
  { socketEvent: "newRound", browserEvent: "socket:newRound" },
  { socketEvent: "manualWinnerWindow", browserEvent: "socket:manualWinnerWindow" },
  { socketEvent: "roundCompleted", browserEvent: "socket:roundCompleted" },
  { socketEvent: "bidUpdate", browserEvent: "socket:bidUpdate" },
  { socketEvent: "placeBid", browserEvent: "socket:placeBid" },
  // Roulette events
  { socketEvent: "ro-timerUpdate", browserEvent: "socket:ro-timerUpdate" },
  { socketEvent: "ro-newRound", browserEvent: "socket:ro-newRound" },
  { socketEvent: "ro-phaseChange", browserEvent: "socket:ro-phaseChange" },
  { socketEvent: "ro-placeBid", browserEvent: "socket:ro-placeBid" },
  { socketEvent: "ro-lastTenWinners", browserEvent: "socket:ro-lastTenWinners" },
  { socketEvent: "ro-manualWinnerConfirmed", browserEvent: "socket:ro-manualWinnerConfirmed" },
  { socketEvent: "ro-manualWinnerWindow", browserEvent: "socket:ro-manualWinnerWindow" },
  { socketEvent: "ro-playSpin", browserEvent: "socket:ro-playSpin" },
  { socketEvent: "ro-roundCompleted", browserEvent: "socket:ro-roundCompleted" },
];

const getStoredToken = () => {
  if (typeof window === "undefined") return null;
  try {
    const authRaw = localStorage.getItem("authUser");
    if (!authRaw) return null;
    const auth = JSON.parse(authRaw);
    return auth?.token || null;
  } catch (error) {
    return null;
  }
};

const SocketProvider = ({ children }) => {
  const listenersRef = useRef([]);
  const tokenRef = useRef(getStoredToken());
  const connectionAttemptRef = useRef(false);

  useEffect(() => {
    const syncSocketAuth = () => {
      const storedToken = getStoredToken();

      if (tokenRef.current === storedToken && socket.connected) {
        return;
      }

      tokenRef.current = storedToken;

      if (storedToken) {
        socket.auth = { ...(socket.auth || {}), token: storedToken };

        // Only connect if not already connected or connecting
        if (!socket.connected && !connectionAttemptRef.current) {
          connectionAttemptRef.current = true;
          socket.connect();

          // Reset connection attempt flag after timeout
          setTimeout(() => {
            connectionAttemptRef.current = false;
          }, 5000);
        }
      } else {
        if (socket.connected) {
          socket.disconnect();
        }
        connectionAttemptRef.current = false;
      }
    };

    // Initial sync
    syncSocketAuth();

    const handleTokenEvent = () => {
      syncSocketAuth();
    };

    // Listen for storage changes and custom auth events
    window.addEventListener("storage", handleTokenEvent);
    window.addEventListener("auth:token", handleTokenEvent);

    // Also listen for socket connection state changes
    const handleConnect = () => {
      connectionAttemptRef.current = false;
    };

    const handleConnectError = (error) => {
      connectionAttemptRef.current = false;
    };

    socket.on("connect", handleConnect);
    socket.on("connect_error", handleConnectError);

    return () => {
      window.removeEventListener("storage", handleTokenEvent);
      window.removeEventListener("auth:token", handleTokenEvent);
      socket.off("connect", handleConnect);
      socket.off("connect_error", handleConnectError);
    };
  }, []);

  useEffect(() => {
    const registerListener = ({ socketEvent, browserEvent }) => {
      const handler = (payload) => {
        let detail = payload;
        if (socketEvent === "connect") {
          detail = { socketId: socket.id };
        } else if (socketEvent === "disconnect") {
          detail = payload || {};
        }

        window.dispatchEvent(new CustomEvent(browserEvent, { detail }));
      };

      socket.on(socketEvent, handler);
      listenersRef.current.push({ socketEvent, handler });
    };

    EVENT_MAP.forEach(registerListener);

    return () => {
      listenersRef.current.forEach(({ socketEvent, handler }) => {
        socket.off(socketEvent, handler);
      });
      listenersRef.current = [];
    };
  }, []);

  useEffect(() => {
    const checkConnection = () => {
      if (!socket.connected) {
        const storedToken = getStoredToken();
        if (storedToken) {
          socket.auth = { token: storedToken };
          socket.connect();
        }
      }
    };

    // Check connection every 5 seconds
    const interval = setInterval(checkConnection, 5000);

    return () => clearInterval(interval);
  }, []);

  return children;
};

export default SocketProvider;